import { relativeCoord } from "@/model/coordinates";

import type { PaintFunctions } from "./PaintLayer";
import { thePaintLayer } from "./PaintLayer";
import type { Line } from "./lineBreaker";
import { breakLines } from "./lineBreaker";

const shortText = { maxLen: 50, smallestFontSize: 0.23, minWordLen: 4 };

interface TextMeasure {
  height: number;
  lines: Line[];
}

export default class TextMeasurer {
  static of(elem: SVGElement | HTMLElement) {
    // assume the elem is a little smaller than real so rounding differences don't lead to cut off text
    const paintFunctions = thePaintLayer.init(elem, relativeCoord(0.98, 0.98));
    return paintFunctions ? new TextMeasurer(paintFunctions) : null;
  }

  private constructor(private paintFunctions: PaintFunctions) {}

  setFontSize(size: number) {
    this.paintFunctions.setElementFontSize(size);
  }

  maxFontSize() {
    return this.paintFunctions.fontSizeForOneLine;
  }

  maxFontSizeForText(): (s: string) => number {
    this.paintFunctions.setCanvasFont(this.maxFontSize());
    return (s) => {
      const chars = Array.from(s);
      return (
        this.paintFunctions.widthInCanvas / this.paintFunctions.charsWidth(chars, 0, chars.length)
      );
    };
  }

  measureLines(size: number, text: string, debug?: boolean): TextMeasure {
    this.paintFunctions.setCanvasFont(size);
    const lines = breakLines(
      text,
      this.paintFunctions.widthInCanvas,
      this.paintFunctions.charsWidth,
    );
    if (debug) {
      this.paintFunctions.displayLines(200, 300, text, lines);
    }
    return { height: lines.length * this.paintFunctions.lineHeight, lines };
  }
}

// downsize fontSize to assure long words in short texts are not wrapped
export function wordDownsize(fontSize: (s: string) => number, text: string) {
  if (text.length > shortText.maxLen) {
    return 1;
  }
  const minWordFontSize = minFontSize(text.split(/\s+/));
  return Math.max(shortText.smallestFontSize, minWordFontSize);

  function minFontSize(ss: string[]): number {
    const wordFontSize = (s: string) => (s.length < shortText.minWordLen ? 1 : fontSize(s));
    return ss.reduce((min, word) => Math.min(min, wordFontSize(word)), 1);
  }
}
